import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight, Send, Copy } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const DEVICE_TYPES = ["iPhone", "Android Phone", "iPad / Tablet", "MacBook / iMac", "Windows Laptop", "Desktop PC", "Other"];
const ISSUES = ["Cracked screen", "Battery / charging", "Water damage", "Won't turn on", "Software / virus", "Speaker / camera", "Data recovery", "Other"];
const URGENCIES = ["Standard", "Same day", "Emergency"];

export default function BookRepair() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const totalSteps = 4;

  const [form, setForm] = useState({
    device_type: "",
    brand_model: "",
    issue_type: "",
    notes: "",
    urgency: "Standard",
    service_type: "in_store",
    postcode: "",
    address_line: "",
    preferred_date: "",
    preferred_time: "",
    full_name: "",
    phone: "",
    email: "",
    preferred_contact: "phone",
    hp_field: "",
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.device_type) return toast.error("Please choose a device type"), false;
      if (!form.brand_model.trim()) return toast.error("Please enter the brand / model"), false;
    }
    if (step === 2) {
      if (!form.issue_type) return toast.error("Please pick the main issue"), false;
    }
    if (step === 3) {
      if (form.service_type === "call_out" && !form.postcode.trim()) return toast.error("Postcode is required for call-out"), false;
    }
    if (step === 4) {
      if (!form.full_name.trim()) return toast.error("Please tell us your name"), false;
      if (!form.phone.trim() || form.phone.replace(/\D/g,"").length < 6) return toast.error("Please enter a valid phone number"), false;
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(s+1, totalSteps)); };
  const back = () => setStep(s => Math.max(s-1, 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, urgency: form.urgency.toLowerCase() };
      if (!payload.email?.trim()) delete payload.email;
      const { data } = await axios.post(`${API}/bookings`, payload);
      setReference(data.reference);
      toast.success("Booking received! We'll contact you shortly.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? (detail[0]?.msg || "Please check the form.") : "Something went wrong, please try again or call us.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div className="container-x section-pad max-w-2xl" data-testid="booking-success">
        <div className="rounded-3xl border border-pink-200 bg-white p-8 shadow-sm text-center">
          <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
          <h1 className="font-display text-3xl font-bold mt-4">Booking received!</h1>
          <p className="mt-2 text-slate-600">Thanks {form.full_name.split(" ")[0]}, one of our team will be in touch shortly to confirm.</p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-pink-200 bg-pink-50 px-4 py-2">
            <span className="text-slate-700 text-sm">Your reference</span>
            <span className="font-mono font-semibold" style={{ color: "var(--icrack-magenta-deep)" }} data-testid="booking-reference">{reference}</span>
            <button onClick={() => { navigator.clipboard.writeText(reference); toast.success("Reference copied"); }} className="ml-1 text-slate-500 hover:text-slate-900" aria-label="Copy">
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href={`tel:${BUSINESS.phones[0].tel}`}><Button variant="outline" className="rounded-xl border-slate-200">Call the shop</Button></a>
            <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-xl border-slate-200">WhatsApp us</Button></a>
            <Button className="rounded-xl btn-primary-magenta" onClick={() => { setReference(null); setStep(1); setForm({ ...form, notes:"", brand_model:"", issue_type:"" }); }}>New booking</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-wash">
      <div className="container-x section-pad max-w-3xl" data-testid="booking-page">
        <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Book a Repair</p>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold">Tell us about your device</h1>
        <p className="mt-2 text-slate-600">Takes under a minute. We'll confirm your booking by phone or WhatsApp.</p>

        <div className="mt-6 flex items-center justify-between text-sm text-slate-600">
          <span data-testid="booking-step-label">Step {step} of {totalSteps}</span>
          <span>{Math.round((step/totalSteps)*100)}% complete</span>
        </div>
        <Progress value={(step/totalSteps)*100} className="mt-2" data-testid="booking-progress" />

        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          {/* Honeypot */}
          <input type="text" tabIndex="-1" autoComplete="off" className="hidden" value={form.hp_field} onChange={(e)=>update("hp_field", e.target.value)} aria-hidden data-testid="booking-honeypot"/>

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="device_type">Device type</Label>
                <Select value={form.device_type} onValueChange={v => update("device_type", v)}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="booking-device-type-select"><SelectValue placeholder="Choose a device" /></SelectTrigger>
                  <SelectContent>{DEVICE_TYPES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="brand_model">Brand &amp; model</Label>
                <Input id="brand_model" placeholder="e.g. iPhone 13 Pro, Samsung S22, MacBook Pro" value={form.brand_model} onChange={e => update("brand_model", e.target.value)} className="h-11 rounded-xl" data-testid="booking-brand-model-input" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <Label>Main issue</Label>
                <RadioGroup value={form.issue_type} onValueChange={v => update("issue_type", v)} className="mt-2 grid grid-cols-2 gap-2" data-testid="booking-issue-type-radio">
                  {ISSUES.map(i => (
                    <label key={i} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.issue_type===i?"border-pink-300 bg-pink-50":"border-slate-200 bg-white"}`}>
                      <RadioGroupItem value={i} id={`iss-${i}`} />
                      <span className="text-sm font-medium">{i}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label htmlFor="notes">Anything else? (optional)</Label>
                <Textarea id="notes" value={form.notes} onChange={e => update("notes", e.target.value)} placeholder="Additional details, damage, symptoms, etc." className="rounded-xl" data-testid="booking-notes-textarea"/>
              </div>
              <div>
                <Label>Urgency</Label>
                <RadioGroup value={form.urgency} onValueChange={v => update("urgency", v)} className="mt-2 grid grid-cols-3 gap-2" data-testid="booking-urgency-radio">
                  {URGENCIES.map(u => (
                    <label key={u} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.urgency===u?"border-pink-300 bg-pink-50":"border-slate-200 bg-white"}`}>
                      <RadioGroupItem value={u} id={`urg-${u}`} />
                      <span className="text-sm font-medium">{u}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <Label>Service type</Label>
                <RadioGroup value={form.service_type} onValueChange={v => update("service_type", v)} className="mt-2 grid grid-cols-2 gap-2" data-testid="booking-service-type-radio">
                  <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer ${form.service_type==="in_store"?"border-pink-300 bg-pink-50":"border-slate-200 bg-white"}`}>
                    <div className="flex items-center gap-2"><RadioGroupItem value="in_store" id="srv-in" /><span className="font-semibold">In-store</span></div>
                    <p className="text-sm text-slate-600">Walk-in at 94 Market Street, Eastleigh.</p>
                  </label>
                  <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer ${form.service_type==="call_out"?"border-pink-300 bg-pink-50":"border-slate-200 bg-white"}`}>
                    <div className="flex items-center gap-2"><RadioGroupItem value="call_out" id="srv-out" /><span className="font-semibold">Call-out</span></div>
                    <p className="text-sm text-slate-600">We come to you. No call-out charge.</p>
                  </label>
                </RadioGroup>
              </div>
              {form.service_type === "call_out" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input id="postcode" value={form.postcode} onChange={e => update("postcode", e.target.value.toUpperCase())} placeholder="e.g. SO50 5RE" className="h-11 rounded-xl" data-testid="booking-postcode-input"/>
                  </div>
                  <div>
                    <Label htmlFor="address_line">Address (optional)</Label>
                    <Input id="address_line" value={form.address_line} onChange={e => update("address_line", e.target.value)} placeholder="Street & house number" className="h-11 rounded-xl" data-testid="booking-address-input"/>
                  </div>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="preferred_date">Preferred date</Label>
                  <Input id="preferred_date" type="date" value={form.preferred_date} onChange={e => update("preferred_date", e.target.value)} className="h-11 rounded-xl" data-testid="booking-date-input"/>
                </div>
                <div>
                  <Label htmlFor="preferred_time">Preferred time</Label>
                  <Input id="preferred_time" type="time" value={form.preferred_time} onChange={e => update("preferred_time", e.target.value)} className="h-11 rounded-xl" data-testid="booking-time-input"/>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="full_name">Full name</Label>
                  <Input id="full_name" value={form.full_name} onChange={e => update("full_name", e.target.value)} className="h-11 rounded-xl" data-testid="booking-full-name-input"/>
                </div>
                <div>
                  <Label htmlFor="phone">Phone number</Label>
                  <Input id="phone" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="07XXX XXX XXX" className="h-11 rounded-xl" data-testid="booking-phone-input"/>
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email (optional)</Label>
                <Input id="email" type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl" data-testid="booking-email-input"/>
              </div>
              <div>
                <Label>Best way to reach you</Label>
                <RadioGroup value={form.preferred_contact} onValueChange={v => update("preferred_contact", v)} className="mt-2 grid grid-cols-3 gap-2" data-testid="booking-preferred-contact-radio">
                  {["phone","whatsapp","email"].map(c => (
                    <label key={c} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer ${form.preferred_contact===c?"border-pink-300 bg-pink-50":"border-slate-200 bg-white"}`}>
                      <RadioGroupItem value={c} id={`pc-${c}`} />
                      <span className="text-sm font-medium capitalize">{c}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            <Button variant="outline" className="rounded-xl border-slate-200" onClick={back} disabled={step===1} data-testid="booking-back-button">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < totalSteps ? (
              <Button className="rounded-xl btn-primary-magenta" onClick={next} data-testid="booking-next-button">
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button className="rounded-xl btn-primary-magenta" onClick={submit} disabled={submitting} data-testid="booking-submit-button">
                {submitting ? "Sending…" : (<><Send className="h-4 w-4 mr-1"/>Submit booking</>)}
              </Button>
            )}
          </div>
        </div>

        <p className="mt-4 text-xs text-slate-500">By submitting you agree to be contacted about your booking. We never share your details.</p>
      </div>
    </div>
  );
}
